import classNames from 'classnames';
import { useState } from 'react';

import { store } from 'Models/Store';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import If from 'Views/Components/If/If';
import { Button, Colors, Display } from '../Button/Button';
import { NewModal } from './NewModal';

export interface INewConfirmModalOptions {
	/** The text to be placed in the confirm button */
	confirmText?: string;
	/** The text to be placed in the cancel button */
	cancelText?: string;
	/** Class name on the modal */
	className?: string;
	/** Class name on the header div */
	headerClassName?: string;
	/** Class name on the message div */
	messageClassName?: string;
	/** Class name on the action div */
	actionClassName?: string;
	/** Class name for the modal overlay */
	overlayClassName?: string;
	/**
	 * If this option is set to true, when the cancel button is pressed the promise is resolved with a value of false
	 * instead being rejected.
	 */
	resolveOnCancel?: boolean;
	/** Boolean indicating if the overlay should close the modal. Defaults to true. */
	shouldCloseOnOverlayClick?: boolean;
	/** Boolean indicating if pressing the esc key should close the modal */
	shouldCloseOnEsc?: boolean;
	resolveOnClose?: boolean;
	showCloseButton?: boolean;
	disableConfirmButton?: boolean;
	disableConfirmButtonFunc?: () => void;
	disableCancelButton?: boolean;
	onBeforeConfirm?: () => boolean | Promise<boolean>;
	hideBodyAndFooter?: boolean;
	hideCancelButton?: boolean;
}

interface IConfirmActionButtonProps {
	onConfirm: () => Promise<void>;
	disabled: boolean;
	children: React.ReactNode;
}

/**
 * The confirm button, holding its own in-flight state so a slow onBeforeConfirm disables the button
 * rather than leaving it live for a second click. The latch in newConfirmModal is what actually
 * guarantees the handler runs once; this is what makes that visible.
 */
function ConfirmActionButton({ onConfirm, disabled, children }: IConfirmActionButtonProps) {
	const [isConfirming, setIsConfirming] = useState(false);

	return (
		<NewModal.ConfirmButton
			onClick={async () => {
				if (isConfirming) {
					return;
				}
				setIsConfirming(true);
				try {
					await onConfirm();
				} finally {
					// The modal is unmounted on success, so this only matters when the confirm was
					// rejected and the modal stayed open - React drops a set on an unmounted component.
					setIsConfirming(false);
				}
			}}
			display={Display.Solid}
			colors={Colors.Secondary}
			disabled={disabled || isConfirming}
		>
			{children}
		</NewModal.ConfirmButton>
	);
}

/**
 * Opens a confirm modal which gives a user an option to confirm or cancel
 * @param title The title on the modal
 * @param message The message to display in the modal
 * @param options Extra options for the modal
 * @returns A promise that is resolved on the user confirming or rejected on the user cancelling
 */
export function newConfirmModal(
	title: string,
	message: React.ReactNode | ((onConfirm: () => void, onCancel: () => void) => React.ReactNode),
	options: INewConfirmModalOptions = {},
) {
	return new Promise<boolean>((resolve, reject) => {
		// The modal's DOM is built once and handed to store.modal, so a second click lands on the same
		// handler while the first await is still in flight. Without this latch onBeforeConfirm ran
		// twice - two POSTs, two success toasts. ConfirmActionButton below disables the button for the
		// same window so the state is visible as well as guarded.
		let confirmInFlight = false;

		const onConfirm = async () => {
			if (confirmInFlight) {
				return;
			}

			// Run validation if provided
			if (options.onBeforeConfirm) {
				confirmInFlight = true;
				try {
					const isValid = await options.onBeforeConfirm();
					if (!isValid) {
						// Don't close modal if validation fails
						return;
					}
				} finally {
					confirmInFlight = false;
				}
			}

			store.modal.hide();
			resolve(true);
		};

		const onCancel = () => {
			store.modal.hide();
			if (options.resolveOnCancel) {
				resolve(false);
			} else {
				reject();
			}
		};

		const confirmText = options.confirmText ?? 'Confirm';
		const cancelText = options.cancelText ?? 'Cancel';

		const onClose = () => {
			store.modal.hide();
			reject();
		};

		const confirmDom: React.ReactNode =
			typeof message === 'function' ? (
				message(onConfirm, onCancel)
			) : (
				<>
					<NewModal.Header className={options.headerClassName}>
						<NewModal.Title>{title}</NewModal.Title>
						<If condition={options.showCloseButton}>
							<Button
								key="header-cancel"
								className="new-modal--close"
								icon={{ icon: 'close', iconPos: 'icon-left' }}
								display={Display.Text}
								colors={Colors.Alternate}
								onClick={() => {
									return options.resolveOnClose ? onClose() : onCancel();
								}}
								labelVisible={false}
							/>
						</If>
					</NewModal.Header>
					<NewModal.Body className={options.messageClassName}>{message}</NewModal.Body>
					<NewModal.Footer className={options.actionClassName}>
						<If condition={!options.hideCancelButton}>
							<NewModal.CancelButton
								onClick={onCancel}
								display={Display.Outline}
								colors={Colors.Grey}
								disabled={options.disableCancelButton ?? false}
							>
								{cancelText}
							</NewModal.CancelButton>
						</If>
						<ConfirmActionButton
							onConfirm={onConfirm}
							disabled={
								(options.disableConfirmButton ||
									(isNotNullOrUndefined(options.disableConfirmButtonFunc) &&
										options?.disableConfirmButtonFunc())) ??
								false
							}
						>
							{confirmText}
						</ConfirmActionButton>
					</NewModal.Footer>
				</>
			);

		store.modal?.show(title, confirmDom, {
			className: classNames('confirm-modal', options.className),
			overlayClassName: options.overlayClassName,
			shouldCloseOnEsc: options.shouldCloseOnEsc ?? false,
			shouldCloseOnOverlayClick: options.shouldCloseOnOverlayClick ?? false,
			onRequestClose: onCancel,
			newModal: true,
		});
	});
}
